// ============================================
// KANBAN COLUMN HELPERS
// ============================================
//
// Shared between the board, the column picker and the empty-state helpers.
// t4+ builds on the new ColumnDef model (filterRules per column); this
// file owns the mapping from a ColumnDef to a "renderable" view used by
// the column header (variant + name) and the predicate that decides
// which column a task belongs to.

import type {
    ColumnDef,
    CustomField,
    FilterRule,
    Task,
} from '@/lib/schemas';
import type { TaskStatus } from '@/lib/schemas';

export type ColumnVariant =
    | 'todo'
    | 'inProgress'
    | 'inReview'
    | 'done'
    | 'cancelled'
    | 'custom';

// RenderableColumn is what the column component actually consumes. It
// augments ColumnDef with the variant tint used by the header badge and
// the display label (which can come from either ColumnDef.name or a
// fallback for the seed status columns).
export interface RenderableColumn {
    id: string;
    name: string;
    variant: ColumnVariant;
    color?: string;
    visible: boolean;
    filterRules: FilterRule[];
}

// Picks a tinted variant for the header badge based on the column's
// name/filterRules. The seeded status columns get the same variants as
// the table view (todo/inProgress/inReview/done/cancelled); everything
// else falls through to a neutral "custom" tint.
function pickVariant(col: ColumnDef): ColumnVariant {
    const first = col.filterRules[0];
    if (first?.field === 'status' && typeof first.value === 'string') {
        switch (first.value) {
            case 'TODO':
                return 'todo';
            case 'IN_PROGRESS':
                return 'inProgress';
            case 'IN_REVIEW':
                return 'inReview';
            case 'DONE':
                return 'done';
            case 'CANCELLED':
                return 'cancelled';
        }
    }
    return 'custom';
}

// Re-export for convenience so other modules can keep importing a single
// stable constant for the default status list.
export const DEFAULT_KANBAN_STATUS_COLUMNS: ColumnDef[] = [
    { id: 'col_todo', name: 'To do', filterRules: [{ field: 'status', operator: 'eq', value: 'TODO' }], visible: true },
    { id: 'col_in_progress', name: 'In progress', filterRules: [{ field: 'status', operator: 'eq', value: 'IN_PROGRESS' }], visible: true },
    { id: 'col_in_review', name: 'In review', filterRules: [{ field: 'status', operator: 'eq', value: 'IN_REVIEW' }], visible: true },
    { id: 'col_done', name: 'Done', filterRules: [{ field: 'status', operator: 'eq', value: 'DONE' }], visible: true },
    { id: 'col_cancelled', name: 'Cancelled', filterRules: [{ field: 'status', operator: 'eq', value: 'CANCELLED' }], visible: true },
];

// Convert a ColumnDef to its RenderableColumn form. Unknown / malformed
// entries are dropped so a stale config can't crash the board.
export function toRenderable(col: ColumnDef): RenderableColumn {
    return {
        id: col.id,
        name: col.name,
        color: col.color,
        variant: pickVariant(col),
        visible: col.visible !== false,
        filterRules: col.filterRules,
    };
}

// Resolve a stored columns payload into renderable columns. When the
// backend normalises to ColumnDef[] the conversion is just a map; when
// the caller still has a legacy string[] payload we rebuild the seed
// status columns in the same way the backend normaliser does.
//
// R2 / R5 contract (caller-respected):
//   - `stored == null` → caller signals "no view selected" (the sidebar's
//     "All tasks" virtual default). Fall back to the canonical 5 status
//     columns so the board has something to render.
//   - `stored === []` (or any empty array) → caller signals "user-created
//     empty view" — return [] so the caller can show its empty-state CTA.
//   - `stored === [entries]` → process the entries into RenderableColumns.
//
// The caller (kanban-view.tsx) is responsible for translating
// `activeView?.config?.kanbanColumns` into the right shape. When
// `activeView` exists but the config has no `kanbanColumns` key (e.g.
// the backend stored `{"filters": {}}` after a fresh view creation),
// the caller passes `[]` here so we return [] and the CTA can fire.
export function resolveKanbanColumns(stored: unknown): RenderableColumn[] {
    if (stored == null) {
        // null / undefined → "no view selected" path. Fall back to the
        // canonical 5 status columns.
        return DEFAULT_KANBAN_STATUS_COLUMNS.map(toRenderable);
    }
    if (!Array.isArray(stored)) return [];
    if (stored.length === 0) {
        // Empty array → "user-created empty view" path. Return [] so
        // the caller renders the "Create your first column" CTA.
        return [];
    }
    const out: RenderableColumn[] = [];
    for (const entry of stored) {
        if (typeof entry === 'string') {
            const match = DEFAULT_KANBAN_STATUS_COLUMNS.find((c) => c.id === `col_${entry.toLowerCase()}`);
            if (match) out.push(toRenderable(match));
            continue;
        }
        if (entry && typeof entry === 'object' && 'id' in entry && 'filterRules' in entry) {
            out.push(toRenderable(entry as ColumnDef));
        }
    }
    return out;
}

// Decide whether `task` belongs in the column described by `col`. Each
// rule must match (logical AND); a missing rule field short-circuits to
// false so unknown fields never silently swallow a task.
export function taskMatchesColumn(
    task: Task,
    col: RenderableColumn,
    customFields: CustomField[]
): boolean {
    return col.filterRules.every((rule) => matchesRule(task, rule, customFields));
}

function matchesRule(task: Task, rule: FilterRule, customFields: CustomField[]): boolean {
    const { field, operator } = rule;
    let actual: unknown;
    if (field === 'status') actual = task.status;
    else if (field === 'priority') actual = task.priority;
    else if (field === 'assignee.id') actual = task.assignees.map((a) => a.userId);
    else if (field.startsWith('customField.')) {
        const cfId = field.slice('customField.'.length);
        actual = task.customFieldValues?.[cfId];
    } else {
        // Default: treat as a custom-field id.
        actual = task.customFieldValues?.[field];
    }

    switch (operator) {
        case 'isEmpty':
            return actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0);
        case 'isNotEmpty':
            return !(actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0));
        case 'eq': {
            if (Array.isArray(actual)) return actual.includes(String(rule.value));
            return String(actual ?? '') === String(rule.value ?? '');
        }
        case 'neq': {
            if (Array.isArray(actual)) return !actual.includes(String(rule.value));
            return String(actual ?? '') !== String(rule.value ?? '');
        }
        case 'in': {
            const allowed = Array.isArray(rule.value) ? rule.value : [String(rule.value ?? '')];
            if (Array.isArray(actual)) return actual.some((v) => allowed.includes(String(v)));
            return allowed.includes(String(actual ?? ''));
        }
        case 'notIn': {
            const blocked = Array.isArray(rule.value) ? rule.value : [String(rule.value ?? '')];
            if (Array.isArray(actual)) return !actual.some((v) => blocked.includes(String(v)));
            return !blocked.includes(String(actual ?? ''));
        }
        case 'contains': {
            const needle = String(rule.value ?? '').toLowerCase();
            if (Array.isArray(actual)) return actual.some((v) => String(v).toLowerCase().includes(needle));
            return String(actual ?? '').toLowerCase().includes(needle);
        }
    }
    return false;
}

// Best-effort conversion of a target RenderableColumn back into a task
// field update. Used by the drag-and-drop handler to decide what to PATCH
// on /api/tasks/:id. Returns null when the column's filterRules don't
// map cleanly onto a single mutable task field (e.g. multi-rule columns
// or assignee filters — the caller should fall back to a no-op with a
// friendly toast).
export interface ColumnFieldUpdate {
    field: 'status' | 'priority' | 'customFieldValues';
    value: unknown;
}

export function columnToTaskUpdate(col: RenderableColumn): ColumnFieldUpdate | null {
    if (col.filterRules.length !== 1) return null;
    const r = col.filterRules[0]!;
    if (r.field === 'status' && typeof r.value === 'string') {
        return { field: 'status', value: r.value as TaskStatus };
    }
    if (r.field === 'priority' && typeof r.value === 'string') {
        return { field: 'priority', value: r.value };
    }
    if (r.field.startsWith('customField.')) {
        const cfId = r.field.slice('customField.'.length);
        return { field: 'customFieldValues', value: { [cfId]: r.value } };
    }
    // Custom-field id (no prefix) for legacy configs.
    if (r.field && !r.field.includes('.')) {
        return { field: 'customFieldValues', value: { [r.field]: r.value } };
    }
    return null;
}
